/**
 * Task reminder sync — schedules local alarms on Android WebView,
 * browser Notification timers on desktop.
 */

const TZ = "Asia/Kolkata";
const browserTimers = new Map();

export function isAndroidWebView() {
    return typeof window !== "undefined" && Boolean(window.BeentaraAndroid);
}

export function reminderPresetFromTask(task) {
    const type = String(task?.reminder_type || "none").toLowerCase();
    if (type === "none") return "none";
    if (type === "at_due") return "at_due";
    if (type === "custom") return "custom";
    if (type === "minutes_before") {
        const mins = Number(task?.reminder_minutes);
        if (mins === 5) return "5";
        if (mins === 10) return "10";
        if (mins === 15) return "15";
        if (mins === 30) return "30";
        if (mins === 60) return "60";
        if (mins === 1440) return "1440";
        return "custom";
    }
    return "none";
}

export function reminderLabelFromTask(task) {
    const preset = reminderPresetFromTask(task);
    const opt = REMINDER_OPTIONS.find((o) => o.value === preset);
    if (preset === "custom" && task?.reminder_minutes) {
        const mins = Number(task.reminder_minutes);
        if (mins >= 1440 && mins % 1440 === 0) return `${mins / 1440} day(s) before`;
        if (mins >= 60 && mins % 60 === 0) return `${mins / 60} hour(s) before`;
        return `${mins} min before`;
    }
    return opt?.label || "No reminder";
}

export const REMINDER_OPTIONS = [
    { value: "none", label: "No reminder" },
    { value: "at_due", label: "At due time" },
    { value: "5", label: "5 minutes before" },
    { value: "10", label: "10 minutes before" },
    { value: "15", label: "15 minutes before" },
    { value: "30", label: "30 minutes before" },
    { value: "60", label: "1 hour before" },
    { value: "1440", label: "1 day before" },
    { value: "custom", label: "Custom…" },
];

export const REPEAT_OPTIONS_EXTENDED = [
    { value: "none", label: "Does not repeat" },
    { value: "daily", label: "Daily" },
    { value: "weekdays", label: "Weekdays" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "annually", label: "Yearly" },
    { value: "custom", label: "Custom…" },
];

export function reminderFieldsFromPreset(preset, customMinutes = 0) {
    const value = String(preset ?? "none").toLowerCase();
    if (value === "none") {
        return { reminder_type: "none", reminder_minutes: 0 };
    }
    if (value === "at_due") {
        return { reminder_type: "at_due", reminder_minutes: 0 };
    }
    if (value === "custom") {
        const mins = Math.max(1, Number(customMinutes) || 15);
        return { reminder_type: "custom", reminder_minutes: mins };
    }
    const mins = Number(value);
    if (Number.isFinite(mins) && mins > 0) {
        return { reminder_type: "minutes_before", reminder_minutes: mins };
    }
    return { reminder_type: "none", reminder_minutes: 0 };
}

export function appendReminderFields(formData, preset, customMinutes = 0) {
    const fields = reminderFieldsFromPreset(preset, customMinutes);
    formData.append("reminder_type", fields.reminder_type);
    formData.append("reminder_minutes", String(fields.reminder_minutes));
    formData.append("timezone", TZ);
}

export function parseTaskDueDateTime(task) {
    if (!task?.due_date) return null;
    const dueRaw = String(task.due_date).trim();
    const timeRaw = task.start_time ? String(task.start_time).trim().slice(0, 5) : "09:00";

    let datePart = dueRaw;
    if (dueRaw.includes("T")) datePart = dueRaw.split("T")[0];
    else if (dueRaw.includes(" ")) datePart = dueRaw.split(" ")[0];

    const iso = `${datePart}T${timeRaw}:00+05:30`;
    const parsed = Date.parse(iso);
    return Number.isNaN(parsed) ? null : parsed;
}

function reminderOffsetMinutes(task) {
    const type = String(task?.reminder_type || "none").toLowerCase();
    if (type === "none") return null;
    if (type === "at_due") return 0;
    const mins = Number(task?.reminder_minutes);
    return Number.isFinite(mins) && mins >= 0 ? mins : null;
}

export function computeTaskReminderFireAt(task) {
    if (!task || task.completed || task.paused) return null;
    if (String(task.status || "").toLowerCase() === "completed") return null;

    const offset = reminderOffsetMinutes(task);
    if (offset === null) return null;

    const dueAt = parseTaskDueDateTime(task);
    if (!dueAt) return null;

    if (task.is_snoozed && task.snooze_until) {
        const snoozeMs = Date.parse(task.snooze_until);
        return Number.isNaN(snoozeMs) ? null : snoozeMs;
    }

    return dueAt - offset * 60 * 1000;
}

export function buildTaskAlarmId(task) {
    if (task?.alarm_id) return String(task.alarm_id);
    const taskId = task?.id;
    const index = task?.occurrence_index ?? 0;
    if (task?.is_snoozed && task?.snooze_until) {
        return `task:${taskId}:snooze:${Date.parse(task.snooze_until)}`;
    }
    return `task:${taskId}:occ:${index}`;
}

export function isRecurringTask(task) {
    if (!task) return false;
    const repetition = String(task.repetition || "none").toLowerCase();
    if (repetition !== "none" && repetition !== "") return true;
    return Boolean(task.repeat_unit);
}

function buildReminderMessages(task) {
    const title = task.title || "Task";
    const offset = reminderOffsetMinutes(task);
    let body = title;
    if (offset === 0) body = `${title} is due now`;
    else if (offset != null && offset > 0) {
        if (offset >= 1440) body = `${title} — due in ${Math.round(offset / 1440)} day(s)`;
        else if (offset >= 60) body = `${title} — due in ${Math.round(offset / 60)} hour(s)`;
        else body = `${title} — due in ${offset} minutes`;
    }
    return { title: `Task: ${title}`, body };
}

export function shouldScheduleReminderOnDevice(task, role) {
    if (!task || task.completed) return false;
    if (String(task.reminder_type || "none").toLowerCase() === "none") return false;
    if (role === "staff") return true;
    // Admin device: only unassigned tasks (assigned staff devices handle their own)
    return !task.assign_to;
}

function nativeSchedule(payload) {
    if (!window.BeentaraAndroid?.scheduleTaskReminder) return false;
    try {
        window.BeentaraAndroid.scheduleTaskReminder(JSON.stringify(payload));
        return true;
    } catch {
        return false;
    }
}

function scheduleBrowserTimer(key, title, body, taskId, triggerAtMs) {
    const delay = triggerAtMs - Date.now();
    if (delay <= 0 || delay > 2147483647) return;

    if (browserTimers.has(key)) clearTimeout(browserTimers.get(key));

    const timerId = setTimeout(() => {
        browserTimers.delete(key);
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
                const n = new Notification(title, {
                    body,
                    icon: "/logo192.png",
                    tag: key,
                    data: { taskId, url: `/tasks?taskId=${taskId}` },
                });
                n.onclick = () => {
                    window.focus();
                    window.location.href = `/tasks?taskId=${taskId}`;
                };
            } catch {
                /* ignore */
            }
        }
    }, delay);

    browserTimers.set(key, timerId);
}

export function cancelTaskReminder(task) {
    const alarmId = buildTaskAlarmId(task);
    if (browserTimers.has(alarmId)) {
        clearTimeout(browserTimers.get(alarmId));
        browserTimers.delete(alarmId);
    }
    if (isAndroidWebView()) {
        window.BeentaraAndroid?.cancelTaskReminder?.(alarmId);
    }
}

export function scheduleTaskReminder(task, role = "staff") {
    if (!shouldScheduleReminderOnDevice(task, role)) {
        cancelTaskReminder(task);
        return false;
    }

    const fireAt = computeTaskReminderFireAt(task);
    if (!fireAt || fireAt <= Date.now()) {
        cancelTaskReminder(task);
        return false;
    }

    const alarmId = buildTaskAlarmId(task);
    const { title, body } = buildReminderMessages(task);
    const payload = {
        alarmId,
        taskId: String(task.id),
        title,
        body,
        atEpochMs: fireAt,
        occurrenceIndex: task.occurrence_index ?? 0,
    };

    cancelTaskReminder(task);

    if (isAndroidWebView()) {
        return nativeSchedule(payload);
    }
    scheduleBrowserTimer(alarmId, title, body, task.id, fireAt);
    return true;
}

export async function ensureTaskNotificationPermission() {
    if (isAndroidWebView()) {
        try {
            window.BeentaraAndroid.requestNotificationPermission?.();
            return window.BeentaraAndroid.hasNotificationPermission?.() === true;
        } catch {
            return false;
        }
    }
    if (typeof Notification === "undefined") return false;
    if (Notification.permission === "granted") return true;
    if (Notification.permission === "denied") return false;
    return (await Notification.requestPermission()) === "granted";
}

export async function syncTaskRemindersFromTasks(tasks = [], { role = "staff" } = {}) {
    if (!Array.isArray(tasks)) return;
    await ensureTaskNotificationPermission();

    const alarms = [];
    const keepIds = new Set();

    tasks.forEach((task) => {
        if (!shouldScheduleReminderOnDevice(task, role)) {
            cancelTaskReminder(task);
            return;
        }

        const fireAt = computeTaskReminderFireAt(task);
        if (!fireAt || fireAt <= Date.now()) {
            cancelTaskReminder(task);
            return;
        }

        const alarmId = buildTaskAlarmId(task);
        keepIds.add(alarmId);
        const { title, body } = buildReminderMessages(task);
        alarms.push({
            alarmId,
            taskId: String(task.id),
            title,
            body,
            atEpochMs: fireAt,
            occurrenceIndex: task.occurrence_index ?? 0,
        });
    });

    if (isAndroidWebView() && window.BeentaraAndroid?.syncTaskReminders) {
        window.BeentaraAndroid.syncTaskReminders(JSON.stringify({ alarms }));
        return;
    }

    // Browser fallback: cancel orphans then schedule each
    browserTimers.forEach((_, key) => {
        if (!keepIds.has(key)) {
            clearTimeout(browserTimers.get(key));
            browserTimers.delete(key);
        }
    });
    alarms.forEach((a) => {
        scheduleBrowserTimer(a.alarmId, a.title, a.body, a.taskId, a.atEpochMs);
    });
}

export async function handleTaskReminderDeepLink({ taskId, action, snooze, apiUrl, getToken, role }) {
    if (!taskId || !apiUrl || !getToken) return null;

    const token = getToken();
    const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
    };

        if (action === "snooze" && snooze) {
            await fetch(`${apiUrl}/task/${taskId}/snooze`, {
                method: "POST",
                headers,
                body: JSON.stringify({ minutes: Number(snooze) || 10 }),
            });
            return { taskId, action: "snooze", minutes: Number(snooze) || 10 };
        }

    if (action === "complete") {
        await fetch(`${apiUrl}/task/${taskId}/complete-occurrence`, {
            method: "POST",
            headers,
        });
        return { taskId, action: "complete" };
    }

    return { taskId, action: action || "open" };
}

const MISSED_GRACE_MS = 5 * 60 * 1000;
const missedNotifiedKeys = new Set();

function missedStorageKey(taskId) {
    const day = new Date().toISOString().slice(0, 10);
    return `beentara_missed_reminder_${taskId}_${day}`;
}

export function findMissedReminders(tasks = [], role = "staff") {
    const now = Date.now();
    return tasks.filter((task) => {
        if (!shouldScheduleReminderOnDevice(task, role)) return false;
        if (String(task.status || "").toLowerCase() === "completed") return false;

        if (task.is_snoozed && task.snooze_until) {
            const snoozeMs = Date.parse(task.snooze_until);
            if (!Number.isNaN(snoozeMs) && snoozeMs > now) return false;
        }

        const dueAt = parseTaskDueDateTime(task);
        if (!dueAt || dueAt > now - MISSED_GRACE_MS) return false;

        const offset = reminderOffsetMinutes(task);
        const reminderAt =
            offset != null && offset > 0 ? dueAt - offset * 60 * 1000 : dueAt;
        return reminderAt <= now - MISSED_GRACE_MS;
    });
}

export function notifyMissedReminders(tasks = [], { role = "staff", onOpenTask } = {}) {
    if (typeof window === "undefined") return [];
    const missed = findMissedReminders(tasks, role);
    const fresh = missed.filter((task) => {
        const key = missedStorageKey(task.id);
        if (missedNotifiedKeys.has(key)) return false;
        if (window.localStorage?.getItem(key) === "1") return false;
        return true;
    });

    fresh.forEach((task) => {
        const key = missedStorageKey(task.id);
        missedNotifiedKeys.add(key);
        try {
            window.localStorage?.setItem(key, "1");
        } catch {
            /* ignore */
        }

        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
                const n = new Notification("Missed Task Reminder", {
                    body: task.title || "A task reminder was missed",
                    icon: "/logo192.png",
                    tag: `missed-task-${task.id}`,
                });
                n.onclick = () => {
                    window.focus();
                    onOpenTask?.(task);
                };
            } catch {
                /* ignore */
            }
        }
    });

    return fresh;
}

export function getAndroidReminderSetupStatus() {
    if (!isAndroidWebView()) {
        return { isAndroid: false, needsSetup: false, hasNotification: true, hasExactAlarm: true };
    }
    const hasNotification = window.BeentaraAndroid?.hasNotificationPermission?.() === true;
    const hasExactAlarm = window.BeentaraAndroid?.canScheduleExactAlarms?.() !== false;
    return {
        isAndroid: true,
        needsSetup: !hasNotification || !hasExactAlarm,
        hasNotification,
        hasExactAlarm,
    };
}

export async function logReminderEventToServer({ apiUrl, getToken, taskId, status, channel, alarmId, expectedFireAt, meta }) {
    if (!apiUrl || !getToken || !taskId) return null;
    try {
        const res = await fetch(`${apiUrl}/task/reminders/log`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${getToken()}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                taskId,
                status,
                channel,
                alarmId,
                expectedFireAt,
                meta,
            }),
        });
        if (!res.ok) return null;
        return res.json();
    } catch {
        return null;
    }
}

export function requestAndroidReminderSetup() {
    if (!isAndroidWebView()) return;
    window.BeentaraAndroid?.requestNotificationPermission?.();
    window.BeentaraAndroid?.requestExactAlarmPermission?.();
}
